import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material';
import { Search, Warning, Analytics } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
  setQuery,
  setCurrentType,
  setUseRag,
  performStandardSearch,
  performIrregularitySearch,
  getIrregularityTypes,
  clearResults,
  clearError,
} from '../store/slices/searchSlice';

// Irregularity type configuration matching the existing system
const IRREGULARITY_TYPES = {
  '': {
    name: 'Standard Search',
    description: 'General search across all government data sources',
    category: 'General',
    icon: '',
    examples: [
      'IT services in Virginia 2024',
      'Construction contracts over $1M',
      'Software licenses by vendor',
    ],
  },
  'sole-source': {
    name: 'Sole Source Awards',
    description: 'Contracts awarded without competition where competitors existed',
    category: 'Competition Issues',
    icon: '',
    examples: [
      'IT services sole source in Maryland',
      'Construction contracts over $1M sole source',
      'Software licenses without competition',
    ],
  },
  'low-competition': {
    name: 'Low Competition',
    description: 'Contracts with limited bidders (2-3 competitors)',
    category: 'Competition Issues',
    icon: '',
    examples: [
      'contracts with only 2 bidders',
      'IT services limited competition 2024',
      'construction awards with 3 or fewer offers',
    ],
  },
  'vendor-concentration': {
    name: 'Vendor Concentration',
    description: 'Single vendor receiving multiple contracts in specific areas',
    category: 'Vendor Issues',
    icon: '',
    examples: [
      'Maryland vendors with multiple contracts NAICS 541511',
      'Texas construction vendor concentration',
      'Single vendor dominance in California',
    ],
  },
  'split-purchase': {
    name: 'Threshold Analysis',
    description: 'Contracts near specific dollar amounts or thresholds',
    category: 'Pricing Issues',
    icon: '',
    examples: [
      'Orders just under $250K threshold',
      'Contracts around $100,000',
      'Awards near micro-purchase limit',
    ],
  },
  'excessive-pricing': {
    name: 'Excessive Pricing',
    description: 'Items priced significantly above market rates',
    category: 'Pricing Issues',
    icon: '',
    examples: [
      'Hammers over $100',
      'Laptops above $3000',
      'Office supplies excessive pricing',
    ],
  },
  'timing-anomaly': {
    name: 'Timing Anomalies',
    description: 'Awards made at suspicious times (weekends, holidays, EOY)',
    category: 'Compliance Issues',
    icon: '',
    examples: [
      'Weekend contract awards',
      'End of fiscal year spending spikes',
      'Holiday period procurement',
    ],
  },
  'emergency-misuse': {
    name: 'Emergency Misuse',
    description: 'Non-emergency items procured as emergency',
    category: 'Compliance Issues',
    icon: '',
    examples: [
      'Emergency procurement abuse',
      'Non-urgent items as emergency',
      'Competition bypass via emergency',
    ],
  },
};

interface UnifiedSearchInterfaceProps {
  onSearch?: (params: any) => void;
  placeholder?: string;
  initialQuery?: string;
  currentIrregularityType?: string;
  showTypeSelector?: boolean;
  showHeader?: boolean;
}

export const UnifiedSearchInterface: React.FC<UnifiedSearchInterfaceProps> = ({
  onSearch,
  placeholder = 'Enter your search query...',
  initialQuery = '',
  currentIrregularityType,
  showTypeSelector = true,
  showHeader = true
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    query,
    loading,
    error,
    currentType,
    useRag,
    totalResults,
  } = useSelector((state: RootState) => state.search);

  const [selectedExample, setSelectedExample] = useState<string>('');
  const [localQuery, setLocalQuery] = useState<string>(initialQuery || query || '');

  // Update local query when initialQuery changes
  useEffect(() => {
    if (initialQuery) {
      setLocalQuery(initialQuery);
      dispatch(setQuery(initialQuery));
    }
  }, [initialQuery, dispatch]);
  
  // Sync with Redux query state if localQuery is empty
  useEffect(() => {
    if (!localQuery && query) {
      setLocalQuery(query);
    }
  }, [query, localQuery]);

  useEffect(() => {
    // Load irregularity types on component mount
    dispatch(getIrregularityTypes());
  }, [dispatch]);

  const handleSearch = async () => {
    const searchQuery = localQuery || query;
    if (!searchQuery.trim()) return;

    dispatch(setQuery(searchQuery));
    dispatch(clearError());

    // Use custom onSearch prop if provided (from InvestigatorPage)
    if (onSearch) {
      const searchParams = {
        query: searchQuery,
        sources: ['all'],
        max_results: 50,
        include_explanation: true,
        use_rag: useRag,
        filters: {},
        irregularity_type: currentIrregularityType
      };
      await onSearch(searchParams);
      return;
    }
    
    // Default behavior for standalone usage
    const effectiveCurrentType = currentIrregularityType || currentType;
    
    if (effectiveCurrentType === '' || !effectiveCurrentType) {
      // Standard search
      dispatch(performStandardSearch({ 
        query: searchQuery, 
        useRag,
        filters: {} 
      }));
    } else {
      // Irregularity-specific search
      dispatch(performIrregularitySearch({
        irregularityType: effectiveCurrentType,
        query: searchQuery,
        maxResults: 50,
        filters: {},
      }));
    }
  };

  const handleExampleQuery = (example: string) => {
    setLocalQuery(example);
    dispatch(setQuery(example));
    setSelectedExample(example);
  };

  const handleTypeChange = (type: string) => {
    dispatch(setCurrentType(type));
    dispatch(clearResults());
    setSelectedExample('');
  };

  const currentTypeConfig = IRREGULARITY_TYPES[currentType as keyof typeof IRREGULARITY_TYPES];
  const isIrregularitySearch = currentType !== '';

  return (
    <Box>
      {/* Header - only show in standalone mode */}
      {showHeader && (
        <Container maxWidth="lg">
          <Box sx={{ py: 4, textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" component="h1" gutterBottom fontWeight={600}>
              MatchAwards Search Investigator
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Reveal Patterns. Flag Irregularities. Protect Federal Spending.
            </Typography>
          </Box>
        </Container>
      )}

      {/* Search Interface */}
      <Box sx={{ mb: 3 }}>
        {/* Irregularity Type Selector - only show if enabled */}
        {showTypeSelector && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Investigation Type</InputLabel>
            <Select
              value={currentType}
              label="Investigation Type"
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              {Object.entries(IRREGULARITY_TYPES).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.icon && <span>{config.icon}</span>}
                    <span>{config.name}</span>
                    {key !== '' && (
                      <Chip 
                        label={config.category} 
                        size="small" 
                        variant="outlined" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Type Description */}
        {showTypeSelector && currentTypeConfig && (
          <Alert 
            severity={isIrregularitySearch ? "warning" : "info"} 
            icon={isIrregularitySearch ? <Warning /> : <Search />}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2">
              <strong>{currentTypeConfig.name}:</strong> {currentTypeConfig.description}
            </Typography>
          </Alert>
        )}

        {/* Search Input */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            value={localQuery}
            onChange={(e) => {
              const newValue = e.target.value;
              setLocalQuery(newValue);
              dispatch(setQuery(newValue));
            }}
            placeholder={placeholder}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            disabled={loading}
            variant="outlined"
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || !localQuery.trim()}
            startIcon={<Search />}
            sx={{ px: 4 }}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
          </Box>

          {/* RAG Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useRag}
                  onChange={(e) => dispatch(setUseRag(e.target.checked))}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {useRag ? 'AI-Enhanced Search' : 'Standard Search'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {useRag 
                      ? 'AI-powered semantic analysis for better fraud detection'
                      : 'Fast keyword-based search'
                    }
                  </Typography>
                </Box>
              }
            />
            {totalResults > 0 && (
              <Typography variant="body2" color="text.secondary">
                Found {totalResults} results
              </Typography>
            )}
          </Box>

          {/* Example Queries - only show in standalone mode */}
        {showTypeSelector && currentTypeConfig && currentTypeConfig.examples.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Try these examples:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {currentTypeConfig.examples.map((example, index) => (
                <Chip
                  key={index}
                  label={example}
                  onClick={() => handleExampleQuery(example)}
                  variant={selectedExample === example ? "filled" : "outlined"}
                  color={selectedExample === example ? "primary" : "default"}
                  clickable
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}

          {/* Loading Progress */}
          {loading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isIrregularitySearch
                  ? `Analyzing contracts for ${currentTypeConfig?.name.toLowerCase()} patterns... (typically 45-55 seconds with AI enhancement)`
                  : useRag 
                    ? 'AI-enhanced search in progress (typically 45-55 seconds)...' 
                    : 'Searching government databases (typically 1-2 seconds)...'
                }
              </Typography>
            </Box>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
      </Box>
    </Box>
  );
};

export default UnifiedSearchInterface;